/**
 * visual-voice-search.js
 * Shopify Görsel + Sesli Arama — Predictive Search Entegrasyonu
 *
 * Çalışma Mantığı:
 *  - Sesli/görsel arama sonucunda mevcut <input.search-input>'a değer yazılır
 *    ve native "input" event'i tetiklenerek temanın kendi predictive-search
 *    bileşeni devreye girer. Ayrı bir sonuç UI'ına gerek kalmaz.
 *
 * Özellikler:
 *  - Web Speech API ile sesli arama
 *  - Google Cloud Vision API ile görsel etiket tespiti
 *  - Erişilebilirlik (ARIA live, focus yönetimi)
 *  - Temiz kaynak kod (IIFE, strict mode)
 */

(function () {
  'use strict';

  // ─── YAPILANDIRMA ──────────────────────────────────────────────────────────
  const CONFIG = {
    // Google Cloud Vision API anahtarı (search-modal.liquid'deki
    // #vvs-config data-google-vision-key attribute'undan okunur)
    googleVisionKey: '',

    // Sesli dinleme maksimum süresi (ms) — konuşma yoksa kapanır
    voiceTimeout: 7000,

    // Vision API'dan kaç etiket alınacak
    maxVisionLabels: 5,
  };

  // ─── ELEMENTLER ────────────────────────────────────────────────────────────
  // Konfigürasyon node'u (API anahtarı burada saklanır)
  const configEl = document.getElementById('vvs-config');
  if (!configEl) return; // Bu snippet yüklü değilse çık

  // Ana arama inputu (temanın predictive search input'u)
  // Hem ID hem class ile dene — farklı tema versiyonlarına karşı dayanıklı
  const searchInput =
    document.getElementById('cmdk-input') ||
    document.querySelector('.search-input[name="q"]');

  if (!searchInput) return;

  const voiceBtn    = document.getElementById('vvs-voice-btn');
  const visualBtn   = document.getElementById('vvs-visual-btn');
  const fileInput   = document.getElementById('vvs-file-input');
  const statusEl    = document.getElementById('vvs-status');
  const previewEl   = document.getElementById('vvs-preview');
  const previewImg  = document.getElementById('vvs-preview-img');
  const previewTags = document.getElementById('vvs-preview-tags');
  const clearBtn    = document.getElementById('vvs-clear-btn');

  // API anahtarını oku
  CONFIG.googleVisionKey = configEl.dataset.googleVisionKey || '';

  // ─── DURUM ─────────────────────────────────────────────────────────────────
  let isListening   = false;
  let recognition   = null;
  let voiceTimer    = null;

  // ─── YARDIMCI: Durum Mesajı ────────────────────────────────────────────────
  /**
   * @param {string} message  - Gösterilecek mesaj. Boş ise sakla.
   * @param {string} type     - 'loading' | 'voice' | 'success' | 'empty' | 'error' | ''
   */
  function setStatus(message, type) {
    statusEl.className = 'vvs-status' + (type ? ' vvs-status--' + type : '');
    statusEl.textContent = message || '';
    statusEl.dataset.visible = message ? 'true' : 'false';
  }

  // ─── YARDIMCI: Temanın Predictive Search'ünü Tetikle ──────────────────────
  /**
   * Verilen metni arama inputuna yazar ve native "input" event'i ateşler.
   * Böylece temanın kendi search JS'i devreye girer.
   */
 function triggerSearch(query) {
  const nativeSetter = Object.getOwnPropertyDescriptor(
    window.HTMLInputElement.prototype, 'value'
  )?.set;

  if (nativeSetter) {
    nativeSetter.call(searchInput, query);
  } else {
    searchInput.value = query;
  }

  // ❌ Eski — bazı web component'lar bunu işlemiyor
  // searchInput.dispatchEvent(new Event('input', { bubbles: true }));

  // ✅ Yeni — native klavye davranışını tam taklit eder
  searchInput.dispatchEvent(
    new InputEvent('input', {
      bubbles: true,
      cancelable: true,
      inputType: 'insertText',
      data: query,
    })
  );
}

  // ─── SESLİ ARAMA (Web Speech API) ─────────────────────────────────────────
  function initVoiceSearch() {
    if (!voiceBtn) return;

    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      // Tarayıcı desteklemiyor — butonu devre dışı bırak
      voiceBtn.disabled = true;
      voiceBtn.setAttribute('aria-disabled', 'true');
      voiceBtn.title = 'Tarayıcınız sesli aramayı desteklemiyor (Chrome önerilir)';
      voiceBtn.style.opacity = '0.35';
      voiceBtn.style.pointerEvents = 'none';
      return;
    }

    recognition = new SpeechRecognition();
    recognition.lang           = document.documentElement.lang || 'tr-TR';
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    recognition.continuous     = false;

    recognition.onstart = () => {
      isListening = true;
      voiceBtn.classList.add('vvs-icon-btn--listening');
      voiceBtn.setAttribute('aria-pressed', 'true');
      setStatus('Sizi dinliyorum… Konuşun', 'voice');

      // Güvenlik: kullanıcı konuşmazsa otomatik kapat
      voiceTimer = setTimeout(() => {
        if (isListening) stopVoice(true);
      }, CONFIG.voiceTimeout);
    };

    recognition.onresult = (event) => {
      const transcript = Array.from(event.results)
        .map(r => r[0].transcript)
        .join('');

      // Geçici sonucu da inputa yaz — kullanıcı ne söylediğini görsün
      triggerSearch(transcript);

      if (event.results[event.results.length - 1].isFinal) {
        stopVoice();
        setStatus('Aranıyor: "' + transcript + '"', 'loading');
        // Kısa bir süre sonra durum mesajını temizle
        setTimeout(() => setStatus('', ''), 2000);
      }
    };

    recognition.onerror = (event) => {
      stopVoice();
      const messages = {
        'not-allowed' : 'Mikrofon izni verilmedi. Tarayıcı ayarlarını kontrol edin.',
        'no-speech'   : 'Ses algılanamadı, tekrar deneyin.',
        'network'     : 'Ağ hatası oluştu.',
        'aborted'     : '', // kullanıcı kendisi durdurdu, mesaj gösterme
      };
      const msg = messages[event.error] ?? 'Ses tanıma hatası: ' + event.error;
      if (msg) setStatus(msg, 'error');
    };

    recognition.onend = () => {
      stopVoice();
    };
  }

  function startVoice() {
    if (!recognition) return;
    try {
      recognition.start();
    } catch {
      // Zaten çalışıyorsa sessizce yoksay
    }
  }

  function stopVoice(timedOut) {
    isListening = false;
    clearTimeout(voiceTimer);
    voiceBtn?.classList.remove('vvs-icon-btn--listening');
    voiceBtn?.setAttribute('aria-pressed', 'false');
    try { recognition?.stop(); } catch { /* noop */ }
    if (timedOut) setStatus('Ses algılanamadı, tekrar deneyin.', 'empty');
  }

  // ─── GÖRSEL ARAMA (Google Cloud Vision API) ───────────────────────────────
  function triggerVisualSearch() {
    if (!CONFIG.googleVisionKey) {
      setStatus(
        'Görsel arama için API anahtarı yapılandırılmamış. ' +
        'Tema ayarlarından "Google Vision API Key" alanını doldurun.',
        'error'
      );
      return;
    }
    fileInput.click();
  }

  async function analyzeImage(file) {
    setStatus('Görsel analiz ediliyor…', 'loading');

    // Önizlemeyi göster
    const objectURL = URL.createObjectURL(file);
    previewImg.src  = objectURL;
    previewEl.hidden = false;
    visualBtn?.classList.add('vvs-icon-btn--active');

    // Belleği serbest bırak (görsel yüklenince)
    previewImg.onload = () => URL.revokeObjectURL(objectURL);

    try {
      const base64 = await fileToBase64(file);

      const res = await fetch(
        `https://vision.googleapis.com/v1/images:annotate?key=${CONFIG.googleVisionKey}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            requests: [{
              image: { content: base64 },
              features: [
                { type: 'LABEL_DETECTION', maxResults: CONFIG.maxVisionLabels },
                { type: 'WEB_DETECTION',   maxResults: 3 },
              ],
            }],
          }),
        }
      );

      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody?.error?.message || 'Vision API yanıt hatası');
      }

      const data     = await res.json();
      const response = data.responses?.[0];

      if (response?.error) {
        throw new Error(response.error.message);
      }

      const labelAnnotations = response?.labelAnnotations || [];
      const webEntities      = (response?.webDetection?.webEntities || [])
        .filter(e => e.score > 0.5 && e.description);

      const labels = [
        ...labelAnnotations.map(l => l.description),
        ...webEntities.map(e => e.description),
      ];

      if (labels.length === 0) {
        setStatus('Görselden etiket tespit edilemedi.', 'empty');
        return;
      }

      // Etiket badge'leri
      const uniqueLabels = [...new Set(labels)].slice(0, CONFIG.maxVisionLabels);
      previewTags.innerHTML = uniqueLabels
        .map(t => `<span class="vvs-tag">${escapeHtml(t)}</span>`)
        .join('');

      // İlk 3 etiketle ara
      const query = uniqueLabels.slice(0, 3).join(' ');
      triggerSearch(query);

      setStatus('Görsel tanındı, aranıyor…', 'loading');
      setTimeout(() => setStatus('', ''), 2000);

    } catch (err) {
      console.error('[VVS] Vision API hatası:', err);
      setStatus('Görsel analiz edilemedi: ' + err.message, 'error');
    }
  }

  /** Dosyayı base64 string'e çevirir (data: prefix olmadan) */
  function fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload  = () => resolve(reader.result.split(',')[1]);
      reader.onerror = () => reject(new Error('Dosya okunamadı'));
      reader.readAsDataURL(file);
    });
  }

  function escapeHtml(str) {
    const d = document.createElement('div');
    d.appendChild(document.createTextNode(String(str)));
    return d.innerHTML;
  }

  // ─── ÖNIZLEME TEMİZLE ──────────────────────────────────────────────────────
  function clearPreview() {
    previewEl.hidden    = true;
    previewImg.src      = '';
    previewTags.innerHTML = '';
    fileInput.value     = '';
    visualBtn?.classList.remove('vvs-icon-btn--active');
    setStatus('', '');
    triggerSearch('');
    searchInput.focus();
  }

  // ─── EVENT LISTENERS ───────────────────────────────────────────────────────

  // Sesli arama butonu
  if (voiceBtn) {
    voiceBtn.setAttribute('aria-pressed', 'false');
    voiceBtn.addEventListener('click', () => {
      if (isListening) {
        stopVoice();
      } else {
        clearPreview();
        startVoice();
      }
    });
  }

  // Görsel arama butonu
  if (visualBtn) {
    // API anahtarı yoksa butonu görsel olarak işaretle ama engelleme
    if (!CONFIG.googleVisionKey) {
      visualBtn.title += ' (API anahtarı eksik)';
      visualBtn.style.opacity = '0.4';
    }
    visualBtn.addEventListener('click', triggerVisualSearch);
  }

  // Dosya seçildiğinde
  if (fileInput) {
    fileInput.addEventListener('change', (e) => {
      const file = e.target.files?.[0];
      if (file) analyzeImage(file);
    });
  }

  // Önizleme temizle
  if (clearBtn) {
    clearBtn.addEventListener('click', clearPreview);
  }

  // Arama inputu sıfırlandığında (temanın reset butonu) önizlemeyi de temizle
  searchInput.addEventListener('input', (e) => {
    if (!e.target.value && previewEl && !previewEl.hidden) {
      clearPreview();
    }
  });

  // ─── BAŞLAT ────────────────────────────────────────────────────────────────
  initVoiceSearch();

})();